import { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../shared/config/apiBase';
import { listProjects, deleteProject } from '../../../shared/services/projects/api';
import { downloadPDFByUrl } from '../../../shared/services/pdf/sharing';
import { generateProjectPdf } from '../../../shared/services/pdf/api';

export function useDashboardProjects({
  currentTab,
  isAuthenticated,
  token,
  triggerToast,
  dismissToast,
  onSessionInvalid,
}) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null);

  const triggerToastRef = useRef(triggerToast);
  const dismissToastRef = useRef(dismissToast);
  const showLoaderTimerRef = useRef(null);
  useEffect(() => {
    triggerToastRef.current = triggerToast;
    dismissToastRef.current = dismissToast;
  }, [triggerToast, dismissToast]);

  const backendBase = API_BASE_URL;

  const resolveAssetUrl = (url, project = null) => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    if (raw.startsWith('/uploads/')) return `${backendBase}${raw}`;
    if (raw.startsWith('uploads/')) return `${backendBase}/${raw}`;

    const normalized = raw.replace(/\\/g, '/');
    const uploadsIdx = normalized.toLowerCase().indexOf('/uploads/');
    if (uploadsIdx >= 0) {
      return `${backendBase}${normalized.slice(uploadsIdx)}`;
    }
    if (project?.userId && /^project-preview-.*\.(png|jpe?g|webp)$/i.test(raw)) {
      return `${backendBase}/uploads/projects/${project.userId}/${raw}`;
    }
    if (/\.pdf$/i.test(raw) && !raw.includes('/') && !raw.includes('\\')) {
      return '';
    }
    return `${backendBase}/${raw.replace(/^\/+/, '')}`;
  };

  const projectNumberMap = useMemo(() => {
    const sorted = [...(projects || [])].sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return ta - tb;
    });

    const map = new Map();
    sorted.forEach((p, i) => {
      map.set(p.id, Number(p?.projectNumber) || (i + 1));
    });
    return map;
  }, [projects]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    if (currentTab !== 'projects' && currentTab !== 'profile') return;

    setProjectsLoading(false);
    if (showLoaderTimerRef.current) clearTimeout(showLoaderTimerRef.current);

    let isCancelled = false;
    showLoaderTimerRef.current = setTimeout(() => {
      if (!isCancelled) setProjectsLoading(true);
    }, 200);

    (async () => {
      try {
        const data = await listProjects();
        setProjects(data?.projects || []);
      } catch (e) {
        if (e?.status === 401 && typeof onSessionInvalid === 'function') {
          onSessionInvalid();
          return;
        }
        console.error(e);
        triggerToastRef.current('Не удалось загрузить проекты', 'error');
      } finally {
        if (showLoaderTimerRef.current) clearTimeout(showLoaderTimerRef.current);
        showLoaderTimerRef.current = null;
        if (!isCancelled) setProjectsLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
      if (showLoaderTimerRef.current) clearTimeout(showLoaderTimerRef.current);
      showLoaderTimerRef.current = null;
    };
  }, [currentTab, isAuthenticated, token, onSessionInvalid]);

  const handleDeleteProject = async (projectId) => {
    try {
      await deleteProject(projectId);
      setProjects((prev) => prev.filter((x) => x.id !== projectId));
      triggerToastRef.current('Проект удален', 'ok');
    } catch (e) {
      console.error(e);
      triggerToastRef.current('Не удалось удалить проект', 'error');
    }
  };

  const downloadProjectPdf = async (projectId) => {
    if (!projectId) return;
    setPdfDownloadingId(projectId);
    triggerToastRef.current('Генерируем PDF...', 'info', { timeout: 0 });
    try {
      await generateProjectPdf(projectId);
      const fileUrl = `${backendBase}/api/project-pdf/${projectId}/file?ts=${Date.now()}`;
      await downloadPDFByUrl(fileUrl);
      triggerToastRef.current('PDF готов, скачивание началось', 'ok');
    } catch (e) {
      console.error('PDF download error:', e);
      const msg = e?.data?.message || e?.message || 'Не удалось скачать PDF';
      triggerToastRef.current(msg, 'error');
    } finally {
      setPdfDownloadingId(null);
    }
  };

  return {
    projects,
    projectsLoading,
    projectNumberMap,
    resolveAssetUrl,
    handleDeleteProject,
    downloadProjectPdf,
    pdfDownloadingId,
  };
}

